import { ActivityItem, ICommandBarItemProps, Icon, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import React from 'react';
import { IProcurementEvent } from '../../../models/Order';
import { useGetRecentProcurementActivitiesQuery } from '../../../store/endpoints/ProcurementEndpoints';
import { IProcurementActivity, IProcurementActivityReference } from '../../../models/Procurement';
import { PROCUREMENT_ACTIVITY_TYPE, ProcurementActivityDisplay } from '../../../Constants';
import Widget from '../../Common/Widgets/Widget';

interface IRecentProcurementActivityWidgetProps {
    onOrderClick: (orderId: string) => void;
    onDemandItemClick: (itemId: string) => void;
    size?: 'small' | 'medium' | 'large';
}

const RecentProcurementActivityWidget: React.FC<IRecentProcurementActivityWidgetProps> = (props) => {
    const activities = useGetRecentProcurementActivitiesQuery({ count: 25 });

    const refetch = () => {
        activities.refetch();
    }

    const rightCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconOnly: true,
            iconProps: { iconName: 'Refresh' },
            onClick: refetch 
        }
    ]


    return (
        <Widget
            header='Recent Activity'
            rightCommands={rightCommands}
            size={props.size || 'small'}
            grow={0}
            >
            {activities.isFetching
                ? <Spinner
                    label='Loading'
                    size={SpinnerSize.large}
                    style={{marginTop: '3em'}}
                    styles={{
                        circle: {
                            width: '90px',
                            height: '90px',
                            borderWidth: '6px'
                        }
                    }}
                    />
                : <Stack tokens={{childrenGap: '1em'}}>
                    {activities.data?.items.map(activity => (
                        <ProcurementActivityItem key={activity.id} activity={activity} onOrderClick={props.onOrderClick} onDemandItemClick={props.onDemandItemClick} />
                    ))}
                </Stack>
            }
            </Widget>

    );
}

interface IProcuremntActivityProps {
    activity: IProcurementActivity;
    onOrderClick: (orderId: string) => void;
    onDemandItemClick: (itemId: string) => void;
}

const ProcurementActivityItem: React.FC<IProcuremntActivityProps> = (props) => {
    const commentStyle: React.CSSProperties = {
        marginTop: '0.25em',
        marginBottom: '0.25em'
    };
    const header = props.activity.user ? `${props.activity.user} - ${props.activity.vendor}` : props.activity.vendor;

    const description = <>
        <strong>{header}</strong>&nbsp;
        {ProcurementActivityDisplay(props.activity.activityType)}.&nbsp;
    </>

    const icon = () => {
        switch (props.activity.activityType) {
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_APPROVED:
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_SUBMITTED:
                return 'Send';
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_SHIPPED:
                return 'DeliveryTruck';
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_DELIVERED:
                return 'Completed';
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_CANCELLED:
                return 'Cancel';
            case PROCUREMENT_ACTIVITY_TYPE.ITEMS_CREATED:
                return 'ProductList';
            case PROCUREMENT_ACTIVITY_TYPE.ORDER_CREATED:
                return 'AddToShoppingList';
            default:
                return 'Info';
        }
    }

    const referenceLink = (reference: IProcurementActivityReference, index: number) => {
        const i = (props.activity.references?.length ?? 0) > 1 ? index+1 : null;
        switch (reference.referenceType) {
            case 'Order':
                return (
                    <span
                        style={{color: '#0064b0ff', cursor: 'pointer', fontWeight: 'bold', marginRight: '0.5em'}}
                        onClick={() => props.onOrderClick(reference.referenceId)}
                        >
                       Order {i}
                    </span>
                )
            case 'DemandItem':
                return (
                    <span
                        style={{color: '#0064b0ff', cursor: 'pointer', fontWeight: 'bold', marginRight: '0.5em'}}
                        onClick={() => props.onDemandItemClick(reference.referenceId)}
                        >
                        Demand Item {i}
                    </span>
                )
            default: return null;
        }
    }

    const references = props.activity.references
        ? <p style={commentStyle}>{props.activity.references?.map(referenceLink)}</p>
        : null;

    return (
        <ActivityItem
            key={props.activity.id}
            activityDescription={description}
            comments={[
                <p style={commentStyle}>{props.activity.message}</p>,
                references
            ]}
            timeStamp={new Date(props.activity.timestamp).toLocaleString()}
            activityIcon={<Icon iconName={icon()} />}
        />
    )
};


export default RecentProcurementActivityWidget;
